import { useEffect, useMemo, useState } from "react";
import { Seo, breadcrumbSchema } from "@/lib/seo";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api, { money } from "@/lib/api";
import { getSolution, SOLUTIONS } from "@/data/solutions";
import { SectionHeading, Reveal, PageLoader, EmptyState } from "@/components/common";
import MarketplaceCard from "@/components/MarketplaceCard";
import {
  ArrowLeft, ArrowRight, Check, Sparkles, LayoutGrid, ArrowUpRight, Star, PenTool,
} from "lucide-react";

export default function SolutionDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const solution = getSolution(slug);
  const [templates, setTemplates] = useState(null);
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTemplates(null);
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => setTemplates([]));
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, [slug]);

  const relevant = useMemo(() => {
    if (!templates || !solution) return [];
    const cats = solution.categories;
    const matched = templates.filter((t) => cats.includes(t.category));
    return matched.length ? matched : templates.slice(0, 3);
  }, [templates, solution]);

  const recommended = packages.find((p) => p.id === solution?.recommended);
  const others = SOLUTIONS.filter((s) => s.slug !== slug).slice(0, 4);

  if (!solution) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <EmptyState icon={LayoutGrid} title="Solution not found" description="Explore all our industry solutions instead."
          action={<Link to="/solutions" className="bg-gold text-navy px-6 py-3 font-semibold">View Solutions</Link>} />
      </div>
    );
  }

  const Icon = solution.icon;

  return (
    <div>
      <Seo
        title={`${solution.title} | ATF WebWorks`}
        description={solution.blurb || solution.tagline}
        path={`/solutions/${slug}`}
        image={solution.image}
        jsonLd={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Solutions", path: "/solutions" }, { name: solution.title, path: `/solutions/${slug}` }])]}
      />
      {/* Hero */}
      <section className="relative h-[62vh] min-h-[440px] flex items-end overflow-hidden grain">
        <img src={solution.image} alt={`${solution.title} — website solutions`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-14 w-full">
          <Link to="/solutions" className="inline-flex items-center gap-2 text-gray-300 hover:text-gold mb-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-testid="back-to-solutions">
            <ArrowLeft size={16} /> All Solutions
          </Link>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
            <div className="w-14 h-14 border border-gold/50 bg-navy/60 backdrop-blur flex items-center justify-center text-gold mb-6">
              <Icon size={26} strokeWidth={1.5} aria-hidden="true" />
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-white leading-[1.05] max-w-3xl">{solution.title}</h1>
            <p className="text-lg text-gray-200 font-light mt-5 max-w-2xl">{solution.blurb}</p>
            <div className="flex flex-wrap gap-4 mt-8">
              <button onClick={() => document.getElementById("sol-templates")?.scrollIntoView({ behavior: "smooth" })} data-testid="view-templates-btn" className="bg-gold text-navy px-7 py-3.5 font-semibold hover:bg-gold-light transition-colors flex items-center gap-2">
                View Designs <ArrowRight size={16} />
              </button>
              <button onClick={() => navigate(`/start?industry=${encodeURIComponent(solution.title)}`)} data-testid="hire-us-btn" className="border border-white/30 text-white px-7 py-3.5 font-medium hover:bg-white/10 transition-colors flex items-center gap-2">
                Hire Us for a Custom Build <ArrowUpRight size={16} />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 space-y-24">
        {/* Outcomes + Includes */}
        <Reveal>
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <SectionHeading label="Why it works" title={solution.tagline} />
              <ul className="mt-8 space-y-4" data-testid="solution-outcomes">
                {solution.outcomes.map((o) => (
                  <li key={o} className="flex items-start gap-4">
                    <span className="w-8 h-8 shrink-0 border border-gold/40 flex items-center justify-center text-gold"><Star size={15} /></span>
                    <span className="text-gray-200 text-lg">{o}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-navy-800 border border-white/10 p-8">
              <h3 className="font-heading text-xl text-white mb-6">What's included</h3>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
                {solution.includes.map((f) => (
                  <div key={f} className="flex items-center gap-3 text-gray-300"><Check size={16} className="text-gold shrink-0" /> <span className="text-sm">{f}</span></div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Templates */}
        <div id="sol-templates" className="scroll-mt-24">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
              <SectionHeading label="Our Work" title={`${solution.title} designs we can build for you`} />
              <Link to="/marketplace" className="text-gold flex items-center gap-2 hover:gap-3 transition-all">Browse all designs <ArrowRight size={16} /></Link>
            </div>
          </Reveal>
          {templates === null ? <PageLoader /> : relevant.length === 0 ? (
            <EmptyState icon={LayoutGrid} title="Custom-built for you" description="We craft this type of site as a fully custom project."
              action={<Link to="/start" className="bg-gold text-navy px-6 py-3 font-semibold">Start a Project</Link>} />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="solution-templates">
              {relevant.map((t, i) => <MarketplaceCard key={t.id} template={t} index={i} />)}
            </div>
          )}
        </div>

        {/* Pricing & custom services */}
        <Reveal>
          <SectionHeading label="Pricing & Services" title="We build your site custom for your business" description="Every site is a custom build tailored to your organization — starting at $997. Browse the designs above for inspiration; we personalize and build yours." />
          <div className="grid lg:grid-cols-3 gap-6 mt-10">
            {recommended && (
              <div className="lg:col-span-2 bg-navy-800 border border-gold/30 p-8" data-testid="recommended-package">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
                  <span className="text-[10px] uppercase tracking-[0.2em] bg-gold text-navy px-3 py-1 font-bold">Recommended for {solution.title}</span>
                  <span className="text-gray-400 text-sm">{recommended.timeline}</span>
                </div>
                <h3 className="font-heading text-3xl text-white mt-3">{recommended.name} Package</h3>
                <div className="font-heading text-4xl text-gold mt-2">{money(recommended.price)}<span className="text-sm text-gray-500 font-body ml-2">from · deposit {money(recommended.deposit)}</span></div>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mt-6">
                  {recommended.deliverables.map((d) => (
                    <div key={d} className="flex items-start gap-3 text-gray-300 text-sm"><Check size={16} className="text-gold shrink-0 mt-0.5" /> {d}</div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-4 mt-8">
                  <button onClick={() => navigate(`/start?industry=${encodeURIComponent(solution.title)}&package=${recommended.name}`)} className="bg-gold text-navy px-7 py-3.5 font-semibold hover:bg-gold-light transition-colors flex items-center gap-2"><PenTool size={16} /> Start Custom Build</button>
                  <Link to="/services" className="border border-white/25 text-white px-7 py-3.5 font-medium hover:bg-white/5 transition-colors">Compare Packages</Link>
                </div>
              </div>
            )}
            <div className="bg-navy-800 border border-white/10 p-8 flex flex-col">
              <Sparkles className="text-gold mb-4" size={22} />
              <h3 className="font-heading text-xl text-white mb-2">Consulting & strategy</h3>
              <p className="text-gray-400 text-sm leading-relaxed flex-1">Not sure which route fits? We'll advise on the best approach for your goals and budget — free of charge.</p>
              <Link to="/start" data-testid="solution-consult" className="mt-6 border border-gold/40 text-gold px-6 py-3 font-medium hover:bg-gold/10 transition-colors flex items-center justify-center gap-2">Book a Consultation <ArrowRight size={15} /></Link>
            </div>
          </div>
        </Reveal>

        {/* Other solutions */}
        <Reveal>
          <SectionHeading label="Keep exploring" title="Other industry solutions" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8" data-testid="other-solutions">
            {others.map((s) => (
              <Link key={s.slug} to={`/solutions/${s.slug}`} className="group flex items-center gap-3 bg-navy-800 border border-white/10 hover:border-gold/50 p-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold">
                <span className="w-10 h-10 shrink-0 border border-gold/40 flex items-center justify-center text-gold"><s.icon size={18} aria-hidden="true" /></span>
                <span className="text-sm text-white group-hover:text-gold transition-colors">{s.title}</span>
              </Link>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
